import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshAlertIcon from "../../../../assets/Dashboard/icons/RefreshAlertIcon";
import InfoIcon from "../../../../assets/images/icons/InfoIcon";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import styles from "./LearningHubWidgetCards.module.scss";
import LearningHubPopup, { EmbedItem } from "./Popup/LearningHubPopup";

const LearningHubWidgetCards = () => {
	const { t } = useTranslation("dashboard", { keyPrefix: "learningHub.card" });
	const [embedItems, setEmbedItems] = useState<LearningHubWidgetCardProps[]>([]);
	const [loadingCards, setLoadingCards] = useState(false);

	const HUBDB_API_URL = "https://api.hubapi.com/hubdb/api/v2/tables/576611574/rows?portalId=25399012";

	const getEmbedItems = async (): Promise<LearningHubWidgetCardProps[]> => {
		const data = await (await fetch(HUBDB_API_URL)).json();

		return data.objects.map(({ values }: any) => ({
			title: values[HUB_DB_COLUMN_MAP.title] || t("untitled"),
			description: values[HUB_DB_COLUMN_MAP.description] || "",
			type: values[HUB_DB_COLUMN_MAP.type]?.name || "info",
			date: new Date(values[HUB_DB_COLUMN_MAP.date]).toLocaleDateString() || "",
			image: values[HUB_DB_COLUMN_MAP.image]?.url,
			embed: {
				embedId: values[HUB_DB_COLUMN_MAP.embedId],
				elementId: values[HUB_DB_COLUMN_MAP.elementId],
			},
			externalLink: values[HUB_DB_COLUMN_MAP.external_link],
		}));
	};

	useEffect(() => {
		setLoadingCards(true);

		getEmbedItems()
			.then(setEmbedItems)
			.finally(() => setLoadingCards(false));
	}, []);

	return (
		<LoadingPlaceholder isLoading={loadingCards}>
			<Box className={styles.cards}>
				{embedItems.map((embedCard, i) => (
					<LearningHubWidgetCard key={i} {...embedCard} />
				))}
			</Box>
		</LoadingPlaceholder>
	);
};

const LearningHubWidgetCard = ({
	type,
	image,
	title,
	date,
	description,
	embed,
	externalLink,
}: LearningHubWidgetCardProps) => {
	const { t } = useTranslation("dashboard", { keyPrefix: "learningHub.card" });
	const [openPopup, setOpenPopup] = useState(false);

	const MAX_DESCRIPTION_LENGTH = 110;

	const handleClick = () => {
		if (externalLink) window.open(externalLink, "_blank");
		else if (embed) setOpenPopup(true);
	};

	const isTruncated = description.length > MAX_DESCRIPTION_LENGTH;
	const displayedDescription = isTruncated
		? description.slice(0, MAX_DESCRIPTION_LENGTH).trim() + "..."
		: description;

	return (
		<>
			<Box
				className={`${styles.card} ${embed || externalLink ? styles.clickableCard : ""}`}
				onClick={handleClick}
			>
				{image ? (
					<img className={styles.image} src={image} style={{ minWidth: 0 }} />
				) : (
					type && <Box className={styles.icon}>{itemTypeIconMap[type]}</Box>
				)}

				<Box className={styles.cardContent}>
					<Box className={styles.cardTexts}>
						<Box className={styles.titleWrapper}>
							<Typography className={styles.cardTitle}>{title}</Typography>
							<Typography className={styles.cardDate}>{date}</Typography>
						</Box>
						<Typography className={styles.cardDescription}>
							{displayedDescription}
							{isTruncated && embed && (
								<BriaButton
									component="span"
									buttonType="textSmall"
									className={styles.readMore}
									onClick={(e) => {
										e.stopPropagation();
										handleClick();
									}}
								>
									{t("readMore")}
								</BriaButton>
							)}
						</Typography>
					</Box>
				</Box>
			</Box>
			{openPopup && embed && (
				<LearningHubPopup open={openPopup} onClose={() => setOpenPopup(false)} embed={embed} />
			)}
		</>
	);
};

export default observer(LearningHubWidgetCards);

type NewsType = "info" | "error";

export type LearningHubWidgetCardProps = {
	title: string;
	description: string;
	date: string;
	type?: NewsType;
	image?: string;
	embed?: EmbedItem;
	externalLink?: string;
};

const itemTypeIconMap: { [type in NewsType]: JSX.Element } = {
	info: <InfoIcon />,
	error: <RefreshAlertIcon />,
};

const HUB_DB_COLUMN_MAP = {
	title: "2",
	description: "3",
	date: "5",
	elementId: "10",
	image: "11",
	embedId: "12",
	type: "13",
	external_link: "14",
};
